/**
 * Heuristic detector for prediction-market-style titles.
 *
 * Morfi is an opinion market platform. Opinion markets ask normative,
 * subjective questions ("Should X?", "Is X overrated?"). Prediction markets
 * ask falsifiable, factual questions ("Will X happen by Y?", "How many Z?").
 *
 * This function returns true when the title strongly reads as a prediction —
 * we use it to surface a soft warning ("are you sure this is an opinion
 * market?"). It must be conservative: false positives nag legitimate opinion
 * questions, so we only fire when the prediction signal is unambiguous AND
 * the title contains no explicit opinion markers.
 *
 * Algorithm: hard-suppress on opinion stems / keywords / phrases, then
 * score-and-threshold the remaining prediction signals. All regexes are
 * compiled once at module load, run on a normalized lowercase string, and
 * are linear-time (no nested quantifiers). Runs in microseconds even at the
 * 200-char title cap.
 */

const OPINION_STARTERS =
  /^(?:should|shouldn['’]t|ought|do you think|is it (?:ethical|right|wrong|fair|moral|immoral|okay|ok|acceptable|appropriate|justified|justifiable|worth|problematic)|was it (?:right|wrong|fair|ethical|justified|worth|a mistake)|is x better|is x worse)\b/;

const OPINION_KEYWORDS =
  /\b(?:overrated|underrated|deserve(?:s|d)?|ethical|unethical|immoral|morally|ought to|should be|shouldn['’]t|deserved|the goat|goated|the best ever|the worst ever|of all time|underappreciated|overappreciated|justifiable|unjustifiable|justified|unjustified|problematic|racist|sexist|misogynist|fascist|cringe|based|woke|toxic|sellout|sellouts)\b/;

const OPINION_PHRASES: RegExp = new RegExp(
  [
    String.raw`\bworth (?:it|buying|having|doing|trying|watching|reading|listening to|paying for|the money|the time|the wait|the hype|the price)\b`,
    String.raw`\b(?:ever |eventually |someday |one day )?be (?:a |an )?(?:good|bad|great|terrible|awful|amazing|wonderful|horrible|fantastic|brilliant|the best|the worst|acceptable|appropriate|justified|justifiable|ethical|moral|fair|unfair|cool|lame|cringe|based|woke|legal|illegal)\b`,
    String.raw`\b(?:was|is|are|were) (?:a |an )?(?:good|bad|great|terrible|awful|amazing|wonderful|horrible|fantastic|brilliant|the best|the worst|fair|unfair) (?:idea|move|decision|call|choice|deal|president|leader|player|coach|manager|ceo|founder|investment|buy|trade|moment|year|era|generation|book|movie|film|album|song|game|show|series|season|fight)\b`,
    String.raw`\b(?:the |a |an )?(?:right|wrong|smart|dumb|stupid|brilliant) (?:call|move|choice|decision|play|trade)\b`,
    String.raw`\b(?:a mistake|a disaster|a flop|a fluke|a scam|a fraud|a hoax|a sham|a joke|funny|boring|cool|lame|cringe|pretty|beautiful|gorgeous|ugly|hideous|a sport)\b`,
    String.raw`\bart\?|\bsport\?`,
  ].join('|'),
);

const PREDICTION_STARTERS =
  /^(?:will|won['’]t|did|didn['’]t|does|has|have|had|could|might|may|when|by when|how (?:many|much|long|soon|high|low|far|fast|big|small|old)|who (?:will|could|might)|who['’]s going to|who is going to|what (?:year|month|date|time|day|price|value|number))\b/;

const COMPARATIVE_PREDICTION_STARTERS =
  /^(?:is|was|are|were)(?:\s+\S+){1,6}\s+(?:going to|gonna|likely to|expected to|projected to|set to|poised to|on track to|about to)\b/;

const YEAR = /\b20[2-9]\d\b/;

const BY_DATE =
  /\bby (?:the end of |end of |start of |beginning of )?(?:20[2-9]\d|jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?|q[1-4]|next (?:week|month|year|quarter)|tomorrow|tonight|monday|tuesday|wednesday|thursday|friday|saturday|sunday)/;

const BEFORE_AFTER =
  /\b(?:before|after) (?:20[2-9]\d|jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/;

const DOLLAR_AMOUNT = /\$\s*\d/;

const NUMERIC_THRESHOLD =
  /\b\d+(?:\.\d+)?\s*(?:%|percent|k|m|b|bn|mil|mn|trillion|billion|million|thousand)\b/i;

const PERCENT_THRESHOLD = /\b\d+(?:\.\d+)?\s*(?:%|percent)\b/i;

const PREDICTIVE_VERBS =
  /\b(?:reach|exceed|surpass|hit|break the record|cross|fall below|drop below|rise above|go above|go below|climb above|sink below|beat|defeat|win the|lose the|graduate|launch|ship|release|announce|be elected|be confirmed|be passed|be approved|resolve|happen|occur|take place|sign|veto|step down|resign|be impeached|qualify|advance|be promoted|be relegated)\b/;

const SUPERLATIVE_FUTURE = /\b(?:first to|next to|first one to)\b/;

const MIN_TITLE_LENGTH = 8;
const FLAG_THRESHOLD = 3;

const SCORE_PREDICTION_STARTER = 3;
const SCORE_COMPARATIVE_FUTURE = 3;
const SCORE_YEAR = 2;
const SCORE_BY_DATE = 2;
const SCORE_BEFORE_AFTER = 2;
const SCORE_PERCENT_THRESHOLD = 2;
const SCORE_DOLLAR_AMOUNT = 1;
const SCORE_NUMERIC_THRESHOLD = 1;
const SCORE_PREDICTIVE_VERB = 1;
const SCORE_SUPERLATIVE_FUTURE = 1;

function normalize(title: string): string {
  return title.trim().replace(/\s+/g, ' ').toLowerCase();
}

export function detectPredictionStyle(title: string): boolean {
  const text = normalize(title);
  if (text.length < MIN_TITLE_LENGTH) return false;

  if (OPINION_STARTERS.test(text)) return false;
  if (OPINION_KEYWORDS.test(text)) return false;
  if (OPINION_PHRASES.test(text)) return false;

  let score = 0;
  if (PREDICTION_STARTERS.test(text)) score += SCORE_PREDICTION_STARTER;
  if (COMPARATIVE_PREDICTION_STARTERS.test(text)) score += SCORE_COMPARATIVE_FUTURE;

  if (YEAR.test(text)) score += SCORE_YEAR;
  if (BY_DATE.test(text)) score += SCORE_BY_DATE;
  if (BEFORE_AFTER.test(text)) score += SCORE_BEFORE_AFTER;

  if (PERCENT_THRESHOLD.test(text)) score += SCORE_PERCENT_THRESHOLD;
  else if (NUMERIC_THRESHOLD.test(text)) score += SCORE_NUMERIC_THRESHOLD;

  if (DOLLAR_AMOUNT.test(text)) score += SCORE_DOLLAR_AMOUNT;
  if (PREDICTIVE_VERBS.test(text)) score += SCORE_PREDICTIVE_VERB;
  if (SUPERLATIVE_FUTURE.test(text)) score += SCORE_SUPERLATIVE_FUTURE;

  return score >= FLAG_THRESHOLD;
}
